<template>
  <form
    autocomplete="off"
    @submit.prevent="$emit('submit')"
  >
    <fieldset class="max-w-full [&_.input-field]:!max-w-full [&_.input-field]:!w-full border-b border-modal-divider p-8 pb-4">
      <div class="input-field">
        <label :class="{ 'text-danger': error?.description }">
          Project Description
          <span class="text-danger">*</span>
        </label>

        <TextEditorInput
          id="description"
          v-model="model.description"
          :validation-error="error?.description"
          :word-limit="1000"
          :inline="false"
        />
      </div>

      <div class="input-field">
        <label :class="{ 'text-danger' : error?.developmentStrategyId}">
          Development Strategy
          <span class="text-danger">*</span>
        </label>

        <AppDropdown
          :trigger-text="selectedStrategyName"
          :class="{ 'border border-danger rounded': error?.developmentStrategyId}"
          placeholder="Select a strategy"
        >
          <template v-if="planStore.content.developmentStrategies.length > 0">
            <AppDropdownItem
              v-for="strategy in planStore.content.developmentStrategies"
              :key="strategy.id"
              class="text-sm"
              @click="model.developmentStrategyId = strategy.id"
            >
              {{ strategy.name }}
            </AppDropdownItem>
          </template>

          <AppDropdownItem
            v-else
            class="text-sm"
          >
            There are no development strategies yet
          </AppDropdownItem>
        </AppDropdown>

        <span
          v-if="error?.developmentStrategyId"
          class="text-xs text-danger"
        >
          {{ error?.developmentStrategyId }}
        </span>
      </div>
    </fieldset>

    <fieldset class="w-full border-b border-modal-divider p-8">
      <label class="font-semibold">
        Project Location
      </label>

      <TextInput
        v-model="model.wardLocation"
        class="max-w-[780px] mb-4 [&_label]:!font-normal [&_label]:!text-sm"
        label="Ward Location"
      />

      <div class="flex items-center max-w-[780px] gap-4 mb-4 [&_label]:!font-normal [&_label]:!text-sm">
        <TextInput
          v-model="model.latitude"
          label="Latitude"
          type="number"
          step="any"
        />

        <TextInput
          v-model="model.longitude"
          label="Longitude"
          type="number"
          step="any"
        />
      </div>

      <div
        class="relative h-[560px] w-full overflow-hidden border border-chrome-light z-0 mb-2"
        :class="{ 'cursor-crosshair': isPlacingMarker }"
      >
        <div
          v-if="!isPlacingMarker"
          class="absolute inset-0 flex items-end justify-center z-[500] pointer-events-none"
        >
          <AppButton
            class="pointer-events-auto btn-secondary btn-solid rounded-full !px-6 mb-2"
            @click="enableMarkerPlacement"
          >
            <FontAwesomeIcon
              class="text-xl"
              :icon="faLocationDot"
            />
            {{ hasValidCoordinates ? 'Change map marker' : 'Add a map marker' }}
          </AppButton>
        </div>

        <div
          v-else
          class="absolute top-4 left-1/2 -translate-x-1/2 z-[500] bg-white px-3 py-1 shadow text-sm font-semibold text-primary"
        >
          Click anywhere on the map
        </div>

        <l-map
          v-model:zoom="zoom"
          v-model:center="center"
          :use-global-leaflet="false"
          :options="{ attributionControl: false }"
          :max-bounds="maxBounds"
          :min-zoom="minZoom"
          :max-bounds-viscosity="1.0"
          @click="onMapClick"
        >
          <l-tile-layer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            layer-type="base"
            name="OpenStreetMap"
          />

          <l-marker
            v-if="hasValidCoordinates"
            :lat-lng="[parseFloat(model.latitude), parseFloat(model.longitude)]"
          />
        </l-map>
      </div>
    </fieldset>

    <fieldset class="w-[800px] p-8">
      <div class="input-field max-w-[240px] mb-6">
        <label>
          Total Project Value (R'000)
        </label>

        <input
          type="text"
          inputmode="decimal"
          :value="formatDisplayValue(model.totalProjectValue)"
          @keypress="allowOnlyNumbers"
          @change="updateTotalProjectValue"
        >
      </div>

      <label class="text-black font-medium mb-3">
        Budget
      </label>

      <AppTable>
        <AppTableHeader>
          <th class="w-[25%]">
            R'000
          </th>

          <th
            v-for="year in mtrefYears"
            :key="year.key"
            class="font-semibold text-right"
          >
            {{ year.offset }} {{ year.label }}
          </th>
        </AppTableHeader>

        <AppTableBody>
          <tr
            v-for="(amounts, type) in model.budget"
            :key="type"
          >
            <td class="capitalize font-semibold">
              {{ type }}
            </td>

            <td
              v-for="year in mtrefYears"
              :key="year.key"
              class="text-right"
            >
              <input
                type="text"
                inputmode="decimal"
                :value="formatDisplayValue(amounts[year.key])"
                @keypress="allowOnlyNumbers"
                @change="updateValue($event, type, year.key)"
              >
            </td>
          </tr>
        </AppTableBody>
      </AppTable>
    </fieldset>
  </form>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { TextEditorInput, TextInput } from '@/components/form-inputs';
import 'leaflet/dist/leaflet.css';
import { LMap, LTileLayer, LMarker } from '@vue-leaflet/vue-leaflet';
import L from 'leaflet';

import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';
import { AppDropdown, AppDropdownItem } from '@/components/dropdowns';
import { AppTable, AppTableBody, AppTableHeader } from '@/components/tables';
import { usePlanStore } from '@/store';
import { useStringFormatter } from '@/composables';
import { mtrefYears } from '@/const';
import { AppButton } from '@/components/app';
import { faLocationDot } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const planStore = usePlanStore();
const stringFormatter = useStringFormatter();

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl,
  iconUrl,
  shadowUrl,
});

defineEmits(['submit']);

defineProps({
  error: {
    type: Object,
    default: () => null,
  },
});

const model = defineModel({
  default: () => ({
    description: null,
    developmentStrategyId: null,
    wardLocation: null,
    latitude: null,
    longitude: null,
    totalProjectValue: null,
    budget: {
      capital: { year1: null, year2: null, year3: null },
    },
  }),
});

const maxBounds = [
  [-90, -180],
  [90, 180],
];

const zoom = ref(6);
const minZoom = ref(2);
const center = ref([-29.7, 24.6]);

const hasValidCoordinates = computed(() => {
  const lat = parseFloat(model.value.latitude);
  const lng = parseFloat(model.value.longitude);
  return !isNaN(lat) && !isNaN(lng);
});

const isPlacingMarker = ref(false);

const enableMarkerPlacement = () => {
  isPlacingMarker.value = true;
};

// Handle Map Click
const onMapClick = (e) => {
  // Only allow placement if the button was clicked first
  if (!isPlacingMarker.value) return;

  model.value.latitude = e.latlng.lat.toFixed(6);
  model.value.longitude = e.latlng.lng.toFixed(6);

  // Turn off placement mode immediately after clicking
  isPlacingMarker.value = false;
};

const allowOnlyNumbers = (event) => {
  const charCode = event.which ? event.which : event.keyCode;

  // Allow: 0-9 (48-57) and dot (46)
  // Block everything else
  if (charCode !== 46 && charCode > 31 && (charCode < 48 || charCode > 57)) {
    event.preventDefault();
  }

  // Prevent adding a second dot if one already exists
  if (charCode === 46 && event.target.value.includes('.')) {
    event.preventDefault();
  }
};

const formatDisplayValue = (value) => {
  if (!value) return '';
  const num = parseFloat(value);
  if (isNaN(num) || num === 0) return '';
  return stringFormatter.financialTableFormatters['currency'].format(value);
};

const cleanNumericInput = (value) => {
  const cleanValue = value.replace(/[^0-9.]/g, '');
  const parsed = parseFloat(cleanValue);

  return isNaN(parsed) ? null : parsed;
};

const updateValue = (event, type, yearKey) => {
  model.value.budget[type][yearKey] = cleanNumericInput(event.target.value);
};

const updateTotalProjectValue = (event) => {
  model.value.totalProjectValue = cleanNumericInput(event.target.value);
};

const selectedStrategyName = computed(() => {
  const strategies = planStore.content.developmentStrategies || [];
  const found = strategies.find(s => s.id === model.value.developmentStrategyId);
  return found ? found.name : '';
});

// Watch coordinates to re-center map if user types manually
watch(
  () => [model.value.latitude, model.value.longitude],
  ([newLat, newLng]) => {
    if (newLat !== null && typeof newLat !== 'string') {
      model.value.latitude = newLat.toString();
    }
    if (newLng !== null && typeof newLng !== 'string') {
      model.value.longitude = newLng.toString();
    }

    const lat = parseFloat(newLat);
    const lng = parseFloat(newLng);

    if (!isNaN(lat) && !isNaN(lng)) {
      if (!isPlacingMarker.value) {
        center.value = [lat, lng];
      }
    }
  },
);
</script>

<style>
/* Leaflet z-index fix if modal/navbars overlap incorrectly */
.leaflet-pane {
  z-index: 0 !important;
}
</style>
